// src/scenes/Agents/AgentDetailPage.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Card, CardContent, Tabs, Tab, Typography,
  TextField, Switch, Slider, Select, MenuItem, InputLabel,
  FormControl, FormControlLabel, Divider,
} from "@mui/material";
import { Save, Delete as DeleteIcon } from "@mui/icons-material";
import Header from "../../components/Header";
import axios from "axios";

// Helper for section tabs
function TabPanel({ value, index, children }) {
  return value === index ? <Box p={2}>{children}</Box> : null;
}

// Cleans empty/undefined fields from a (shallow) object
function cleanObject(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, v]) =>
        v !== undefined &&
        v !== "" &&
        !(Array.isArray(v) && v.length === 0) &&
        !(typeof v === "object" && v !== null && Object.keys(v).length === 0)
    )
  );
}

export default function AgentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [raw, setRaw] = useState(null);
  const [voices, setVoices] = useState([]);
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(null);

  // Fetch agent details
  useEffect(() => {
    axios.get(`/api/vapi/assistants/${id}`)
      .then(r => {
        setRaw(r.data);
        setForm(mapRawToForm(r.data));
      })
      .catch(console.error);
  }, [id]);

  // Fetch available voices
  useEffect(() => {
    axios.get('/api/vapi/voices')
      .then(r => setVoices(r.data))
      .catch(console.error);
  }, []);

  // Tab switch
  const handleTab = (e, v) => setTab(v);

  // --- Mapping Functions ---
  function mapRawToForm(raw) {
    return {
      name: raw.name || "",
      description: raw.description || "",
      firstMessage: raw.firstMessage || raw.model?.firstMessage || "",
      systemPrompt: raw.model?.model?.messages?.find(m => m.role === "system")?.content
        || raw.model?.messages?.find(m => m.role === "system")?.content
        || "",
      maxTokens: raw.model?.model?.maxTokens ?? raw.model?.maxTokens ?? 250,
      temperature: raw.model?.model?.temperature ?? raw.model?.temperature ?? 0.5,
      model: raw.model?.model?.model || raw.model?.model || "",
      modelProvider: raw.model?.model?.provider || raw.model?.provider || "openai",
      voiceId: raw.voice?.voiceId || "",
      voiceProvider: raw.voice?.provider || "vapi",
      backgroundSound: raw.backgroundSound || "",
      backgroundSoundUrl: raw.backgroundSoundUrl || "",
      inputMinCharacters: raw.voice?.inputMinCharacters ?? "",
      punctuationBoundaries: raw.voice?.inputPunctuationBoundaries || [],
      transcriber: raw.transcriber || {},
      tools: raw.tools || [],
      analysis: raw.analysis || {},
      privacy: raw.privacy || {},
      startSpeakingPlan: raw.startSpeakingPlan || {},
      voicemailDetection: raw.voicemailDetection || {},
      stopSpeakingPlan: raw.stopSpeakingPlan || {},
      callTimeoutSettings: raw.callTimeoutSettings || {},
      keypadInputSettings: raw.keypadInputSettings || {},
      messaging: raw.messaging || {},
      voicemailMessage: raw.voicemailMessage || "",
      endCallFunctionEnabled: !!raw.endCallFunctionEnabled,
      endCallMessage: raw.endCallMessage || "",
      forwardingPhoneNumber: raw.forwardingPhoneNumber || "",
      clientMessages: raw.clientMessages || [],
      serverMessages: raw.serverMessages || [],
      server: raw.server || {},
    };
  }

  function mapFormToVapi(form) {
    const payload = {
      name: form.name,
      description: form.description,
      firstMessage: form.firstMessage,
      model: cleanObject({
        provider: form.modelProvider,
        model: form.model,
        maxTokens: form.maxTokens,
        temperature: form.temperature,
        messages: [
          { role: "system", content: form.systemPrompt }
        ]
      }),
      voice: cleanObject({
        voiceId: form.voiceId,
        provider: form.voiceProvider,
        inputPunctuationBoundaries: form.punctuationBoundaries,
        inputMinCharacters: form.inputMinCharacters
      }),
      backgroundSound: form.backgroundSound,
      backgroundSoundUrl: form.backgroundSoundUrl,
      transcriber: form.transcriber,
      tools: form.tools,
      analysis: form.analysis,
      privacy: form.privacy,
      startSpeakingPlan: form.startSpeakingPlan,
      voicemailDetection: form.voicemailDetection,
      stopSpeakingPlan: form.stopSpeakingPlan,
      callTimeoutSettings: form.callTimeoutSettings,
      keypadInputSettings: form.keypadInputSettings,
      messaging: form.messaging,
      voicemailMessage: form.voicemailMessage,
      endCallFunctionEnabled: !!form.endCallFunctionEnabled,
      endCallMessage: form.endCallMessage,
      forwardingPhoneNumber: form.forwardingPhoneNumber,
      clientMessages: form.clientMessages,
      serverMessages: form.serverMessages,
      server: form.server,
    };
    return cleanObject(payload); // Remove all empty/undefined keys
  }

  // --- Form field handling ---
  function setVal(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }
  function handleChange(e) {
    const { name, value } = e.target;
    setVal(name, value);
  }
  function handleSlider(name) {
    return (_, v) => setVal(name, v);
  }
  function handleSwitch(e) {
    setVal(e.target.name, e.target.checked);
  }

  if (!form) return <div>Loading…</div>;

  // --- UI ---
  return (
    <Box m="20px">
      <Header title={`Edit: ${form.name}`} subtitle="Modify your assistant below" />
      <Card variant="outlined">
        <CardContent>
          <Tabs value={tab} onChange={handleTab} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
            <Tab label="General" />
            <Tab label="Model" />
            <Tab label="Voice" />
            <Tab label="Messaging" />
            <Tab label="Advanced" />
          </Tabs>

          {/* General */}
          <TabPanel value={tab} index={0}>
            <TextField fullWidth label="Name" name="name" value={form.name} onChange={handleChange} sx={{ mb: 2 }} />
            <TextField fullWidth label="Description" name="description" value={form.description} onChange={handleChange} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} maxRows={6} label="First Message" name="firstMessage" value={form.firstMessage} onChange={handleChange} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={8} maxRows={20} label="System Prompt" name="systemPrompt" value={form.systemPrompt} onChange={handleChange} sx={{ mb: 2 }} />
          </TabPanel>

          {/* Model */}
          <TabPanel value={tab} index={1}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Model Provider</InputLabel>
              <Select value={form.modelProvider} label="Model Provider" name="modelProvider" onChange={handleChange}>
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="anthropic">Anthropic</MenuItem>
                <MenuItem value="vapi">Vapi</MenuItem>
                {/* Add other providers if supported */}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Model</InputLabel>
              <Select value={form.model} label="Model" name="model" onChange={handleChange}>
                <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                <MenuItem value="gpt-4o">gpt-4o</MenuItem>
                {/* Populate with all available models */}
              </Select>
            </FormControl>
            <Box sx={{ mb: 2 }}>
              <Typography gutterBottom>Max Tokens ({form.maxTokens})</Typography>
              <Slider min={50} max={4000} value={form.maxTokens} onChange={handleSlider("maxTokens")} />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography gutterBottom>Temperature ({form.temperature})</Typography>
              <Slider step={0.01} min={0} max={1} value={form.temperature} onChange={handleSlider("temperature")} />
            </Box>
          </TabPanel>

          {/* Voice */}
          <TabPanel value={tab} index={2}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Voice Provider</InputLabel>
              <Select value={form.voiceProvider} label="Voice Provider" name="voiceProvider" onChange={handleChange}>
                <MenuItem value="vapi">Vapi</MenuItem>
                <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Voice</InputLabel>
              <Select value={form.voiceId} name="voiceId" label="Voice" onChange={handleChange}>
                {voices.map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Background Sound</InputLabel>
              <Select value={form.backgroundSound} name="backgroundSound" label="Background Sound" onChange={handleChange}>
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="office">Office</MenuItem>
                <MenuItem value="cafe">Cafe</MenuItem>
                <MenuItem value="rain">Rain</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth label="Background Sound URL" name="backgroundSoundUrl" value={form.backgroundSoundUrl} onChange={handleChange} sx={{ mb: 2 }} />
            <TextField fullWidth label="Input Min Characters" name="inputMinCharacters" type="number" value={form.inputMinCharacters} onChange={handleChange} sx={{ mb: 2 }} />
            <TextField fullWidth label="Punctuation Boundaries" name="punctuationBoundaries" value={form.punctuationBoundaries.join(",")} onChange={e => setVal("punctuationBoundaries", e.target.value.split(",").map(s => s.trim()))} sx={{ mb: 2 }} />
          </TabPanel>

          {/* Messaging */}
          <TabPanel value={tab} index={3}>
            <TextField fullWidth label="Voicemail Message" name="voicemailMessage" value={form.voicemailMessage} onChange={handleChange} sx={{ mb: 2 }} />
            <TextField fullWidth label="End Call Message" name="endCallMessage" value={form.endCallMessage} onChange={handleChange} sx={{ mb: 2 }} />
            <FormControlLabel
              control={<Switch checked={!!form.endCallFunctionEnabled} onChange={handleSwitch} name="endCallFunctionEnabled" />}
              label="Enable End Call Function"
              sx={{ mb: 2 }}
            />
            <TextField fullWidth label="Forwarding Phone Number" name="forwardingPhoneNumber" value={form.forwardingPhoneNumber} onChange={handleChange} sx={{ mb: 2 }} />
            {/* Add more fields for clientMessages, serverMessages, etc if desired */}
          </TabPanel>

          {/* Advanced */}
          <TabPanel value={tab} index={4}>
            <TextField fullWidth multiline minRows={2} label="Transcriber (JSON)" value={JSON.stringify(form.transcriber, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Tools (JSON)" value={JSON.stringify(form.tools, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Analysis (JSON)" value={JSON.stringify(form.analysis, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Privacy (JSON)" value={JSON.stringify(form.privacy, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Start Speaking Plan (JSON)" value={JSON.stringify(form.startSpeakingPlan, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Voicemail Detection (JSON)" value={JSON.stringify(form.voicemailDetection, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Stop Speaking Plan (JSON)" value={JSON.stringify(form.stopSpeakingPlan, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Call Timeout Settings (JSON)" value={JSON.stringify(form.callTimeoutSettings, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Keypad Input Settings (JSON)" value={JSON.stringify(form.keypadInputSettings, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Messaging (JSON)" value={JSON.stringify(form.messaging, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            <TextField fullWidth multiline minRows={2} label="Server (JSON)" value={JSON.stringify(form.server, null, 2)} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
            {/* Optionally, make these JSON-editable with a code editor */}
          </TabPanel>
        </CardContent>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", gap: 2, m: 2 }}>
          <Button
            startIcon={<Save />}
            variant="contained"
            color="primary"
            onClick={async () => {
              try {
                const payload = mapFormToVapi(form);
                console.log("PATCHING PAYLOAD:", payload);
                const { data } = await axios.patch(`/api/vapi/assistants/${id}`, payload);
                setRaw(data);
                setForm(mapRawToForm(data)); // update with server response
                alert("Updated successfully!");
              } catch (e) {
                console.error("Update error:", e.response?.data || e.message);
                alert("Update failed: " + (e.response?.data?.error || e.message));
              }
            }}
          >Save</Button>
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
            onClick={() => {
              if (!window.confirm("Delete?")) return;
              axios.delete(`/api/vapi/assistants/${id}`)
                .then(() => navigate("/agents"))
                .catch(e => alert("Delete failed: " + e.message));
            }}
          >Delete</Button>
        </Box>
      </Card>
    </Box>
  );
}
